import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Oval } from "react-loader-spinner";
import PropTypes from "prop-types";
import { Edit, Delete, Add } from "@mui/icons-material";
import {
  ViewButton,
  DeleteButton,
  StyledTypography,
  StyledListItemText,
  StyledButton,
} from "components/StyledComponents/styledComponents.js";
import URLs from "constants/urls";
import Cookie from "js-cookie";

const DSABankAccountsDialog = ({ open, onClose, dsaId, setAlert }) => {
  const jwt_token = Cookie.get("jwt_token");
  const [formData, setFormData] = useState({
    bankName: "",
    accountNumber: "",
    ifsc: "",
    sac: "",
    isDefault: true,
    dsaid: 0,
    bankID: 0,
  });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [proccessing, setProcessing] = useState(false);

  useEffect(() => {
    if (open) {
      fetchBankAccountsData();
    }
  }, [open, dsaId]);
  const resetForm = () => {
    setFormData({
      bankName: "",
      accountNumber: "",
      ifsc: "",
      sac: "",
      isDefault: true,
      dsaid: 0,
      bankID: 0,
    });
  };

  const fetchBankAccountsData = async () => {
    setProcessing(true);
    const url = `${URLs.bankAccountsByDsaId}/${dsaId}`;
    const options = {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt_token}`,
      },
    };
    try {
      const response = await fetch(url, options);
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setBankAccounts(data);
    } catch (error) {
      setAlert("Failed to fetch bank accounts", "error");
    }
    setProcessing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    setProcessing(true);
    try {
      const method = editingAccount ? "PUT" : "POST";
      const url = editingAccount ? `${URLs.dsaBankAccountUrl}` : `${URLs.dsaBankAccountUrl}`;
      const formattedFormData = {
        ...formData,
        isDefault: formData.isDefault ? "Y".charAt(0) : "N".charAt(0),
        dsaid: dsaId,
      };
      const options = {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
        body: JSON.stringify(formattedFormData),
      };

      const response = await fetch(url, options);
      if (!response.ok) throw new Error("Failed to save bank account");
      resetForm();
      fetchBankAccountsData();
      setEditingAccount(null);
      setAlert("Bank account saved successfully", "success");
    } catch (error) {
      setAlert("Failed to save bank account", "error");
    }
    setProcessing(false);
  };

  const handleDelete = async (dId, bId) => {
    setProcessing(true);
    try {
      const options = {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt_token}`,
        },
      };
      await fetch(`${URLs.dsaBankAccountUrl}/${dId}/${bId}`, options);
      fetchBankAccountsData();
      resetForm();
      setAlert("Bank account deleted successfully", "success"); // Success message
    } catch (error) {
      console.error("Error deleting bank account:", error);
      setAlert("Failed to delete bank account", "error"); // Error message
    }
    setProcessing(false);
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      bankName: account.bankName,
      accountNumber: account.accountNumber,
      ifsc: account.ifsc,
      sac: account.sac,
      isDefault: account.isDefault,
      dsaid: account.dsaid,
      bankID: account.bankID,
    });
    setShowAddForm(true);
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <StyledTypography>Manage Bank Accounts</StyledTypography>
      <DialogContent>
        <List>
          {proccessing ? (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%", // Ensures loader is centered
              }}
            >
              <Oval
                height={40}
                width={40}
                color="#3498db"
                visible={true}
                ariaLabel="oval-loading"
              />
            </div>
          ) : (
            Array.isArray(bankAccounts) &&
            bankAccounts.map((account) => (
              <ListItem key={account.id} sx={{ padding: "5px" }}>
                <StyledListItemText primary={account.bankName} />
                <ViewButton onClick={() => handleEdit(account)}>
                  <Edit />
                </ViewButton>
                <DeleteButton onClick={() => handleDelete(account.dsaid, account.bankID)}>
                  <Delete />
                </DeleteButton>
              </ListItem>
            ))
          )}
        </List>

        {showAddForm && (
          <>
            <TextField
              label="Bank Name"
              name="bankName"
              fullWidth
              margin="dense"
              value={formData.bankName}
              onChange={handleInputChange}
            />
            <TextField
              label="Account Number"
              name="accountNumber"
              fullWidth
              margin="dense"
              value={formData.accountNumber}
              onChange={handleInputChange}
            />
            <TextField
              label="IFSC"
              name="ifsc"
              fullWidth
              margin="dense"
              value={formData.ifsc}
              onChange={handleInputChange}
            />
            <TextField
              label="SAC"
              name="sac"
              fullWidth
              margin="dense"
              value={formData.sac}
              onChange={handleInputChange}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                  name="isDefault"
                />
              }
              label="Set as Default"
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton
          onClick={() => {
            onClose();
            setShowAddForm(false);
            setEditingAccount(null);
            resetForm();
          }}
        >
          Cancel
        </StyledButton>
        {!showAddForm ? (
          <StyledButton
            variant="outlined"
            color="primary"
            onClick={() => setShowAddForm(true)}
            startIcon={<Add />}
          >
            Add Bank Account
          </StyledButton>
        ) : (
          <StyledButton onClick={handleSubmit} variant="contained" color="primary">
            Save
          </StyledButton>
        )}
      </DialogActions>
    </Dialog>
  );
};

DSABankAccountsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  dsaId: PropTypes.string.isRequired,
  setAlert: PropTypes.func.isRequired,
};

export default DSABankAccountsDialog;
